import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import {
  Box,
  Dialog,
  IconButton,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Clear, Close, Search } from '@mui/icons-material';
import CustomButton from '../widgets/CustomButton';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';
import ImageShimmer from '../widgets/ImageShimmer';
import CustomTabs from '../widgets/CustomTabs';
import WatchlistWidget from '../widgets/WatchlistWidget';
import PublicReviewCard, { type PublicReviewCardData } from '../widgets/PublicReviewCard';
import { glassStyles } from '../../tokens/designTokens';
import { font } from '../../utils/fontHelper';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../utils/ToastProvider';
import { useUserWatchlistData, type Watchlist } from '../../contexts/UserWatchlistDataContext';
import {
  createStory,
  createWatchlistStory,
  getMyStories,
  getReviewStoryId,
  getWatchlistStoryId,
} from '../../services/storiesService';
import { invalidateStoriesCache } from '../widgets/StoriesRail';
import { movieService, movieCacheService } from '@watchlist/shared';
import { parseFirebaseDate } from '@watchlist/shared/lib/utils/dateUtils';
import type { UserMovieData } from '@watchlist/shared/src/types';

const TMDB_W185 = 'https://image.tmdb.org/t/p/w185';
const TMDB_W780 = 'https://image.tmdb.org/t/p/w780';
const TMDB_BACKDROP = 'https://image.tmdb.org/t/p/w1280';

const TABS = [
  { label: 'Reviews', value: 0 },
  { label: 'Watchlists', value: 1 },
];

interface ReviewRow {
  movieId: string;
  movieTitle: string;
  posterPath: string | null;
  backdropPath: string | null;
  director: string | null;
  releaseYear: number | null;
  rating: number | null;
  reviewText: string;
  watchedDate?: string;
  watchedDatePrecision?: 'day' | 'month' | 'year';
  updatedAt?: any;
}

type Preview =
  | { kind: 'review'; row: ReviewRow }
  | { kind: 'watchlist'; wl: Watchlist; topPosterPaths: string[] };

interface AddStoryBottomSheetProps {
  open: boolean;
  onClose: () => void;
}

function getPreviewGridShape(count: number): { cols: number; rows: number } {
  if (count <= 1) return { cols: 1, rows: 1 };
  if (count === 2) return { cols: 2, rows: 1 };
  if (count === 3) return { cols: 3, rows: 1 };
  if (count === 4) return { cols: 2, rows: 2 };
  if (count <= 6) return { cols: 3, rows: 2 };
  if (count <= 9) return { cols: 3, rows: 3 };
  if (count <= 12) return { cols: 4, rows: 3 };
  return { cols: 4, rows: 4 };
}

// ─── Story Preview ────────────────────────────────────────────────────────────

interface StoryPreviewProps {
  preview: Preview;
  isDark: boolean;
  posting: boolean;
  onBack: () => void;
  onPost: () => void;
}

const StoryPreview: React.FC<StoryPreviewProps> = ({ preview, isDark, posting, onBack, onPost }) => {
  const posterUrl =
    preview.kind === 'review'
      ? preview.row.posterPath ? `${TMDB_W780}${preview.row.posterPath}` : null
      : preview.wl.movieDetails[0]?.poster ? `${TMDB_W185}${preview.wl.movieDetails[0].poster}` : null;

  const backdropUrl =
    preview.kind === 'review' && preview.row.backdropPath
      ? `${TMDB_BACKDROP}${preview.row.backdropPath}`
      : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', overflow: 'hidden' }}>
      {/* Blurred background */}
      <Box sx={{ position: 'absolute', inset: 0 }}>
        {posterUrl ? (
          <ImageShimmer
            src={posterUrl}
            alt=""
            width="100%" height="100%" objectFit="cover"
            style={{ filter: 'blur(18px) brightness(0.35)', transform: 'scale(1.08)' }}
          />
        ) : (
          <Box sx={{
            width: '100%', height: '100%',
            background: isDark
              ? 'linear-gradient(135deg, #1a0a2e 0%, #0d0d1a 100%)'
              : 'linear-gradient(135deg, #2a1550 0%, #0d0d2a 100%)',
          }} />
        )}
        {/* bottom-fade so the CTA bar is readable */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 60%, rgba(0,0,0,0.75) 100%)',
        }} />
      </Box>

      {/* Back button */}
      <Box sx={{ position: 'relative', zIndex: 10, px: 2, pt: 1.5 }}>
        <CustomButton
          semantic="tertiary"
          size="small"
          icon={<ArrowLeftIcon />}
          onClick={onBack}
          sx={{ color: '#fff' }}
        >
          Back
        </CustomButton>
      </Box>

      {/* Glass card — scrollable when tall */}
      <Box sx={{
        position: 'relative', zIndex: 10,
        flex: 1,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        px: 2.5,
        py: 2,
        overflowY: 'auto',
      }}>
        <Box sx={{
          width: '100%',
          maxWidth: 380,
          ...glassStyles.dialog(isDark),
          borderRadius: 4,
          p: 0,
          overflow: 'hidden',
        }}>
          {/* Backdrop image (review only) */}
          {backdropUrl && (
            <Box sx={{ height: 160, flexShrink: 0 }}>
              <ImageShimmer src={backdropUrl} alt="" width="100%" height="100%" objectFit="cover" />
            </Box>
          )}

          <Box sx={{ px: 2.5, pt: backdropUrl ? 1.5 : 2, pb: 2.5 }}>
            {preview.kind === 'watchlist' ? (() => {
              const MAX = 16;
              const displayPaths = preview.topPosterPaths.slice(0, MAX);
              const movieCount = preview.wl.movieDetails.length;
              const overflowCount = movieCount - MAX;
              const { cols, rows } = getPreviewGridShape(displayPaths.length);
              const totalSlots = cols * rows;
              const slots = [...displayPaths];
              while (slots.length < totalSlots) slots.push('');
              return (
                <>
                  <Typography sx={{ fontFamily: font(), fontWeight: 700, fontSize: 15, color: '#fff', lineHeight: 1.3, mb: 0.25 }}>
                    {preview.wl.name}
                  </Typography>
                  <Typography sx={{ fontFamily: font(), fontSize: 12, color: 'rgba(255,255,255,0.6)', mb: preview.wl.description ? 1 : displayPaths.length > 0 ? 1.5 : 0 }}>
                    {movieCount} {movieCount === 1 ? 'movie' : 'movies'}
                  </Typography>
                  {preview.wl.description && (
                    <Typography sx={{ fontFamily: font(), fontSize: 13, lineHeight: 1.55, color: 'rgba(255,255,255,0.85)', mb: displayPaths.length > 0 ? 1.5 : 0, display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {preview.wl.description}
                    </Typography>
                  )}
                  {displayPaths.length > 0 && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 0.5, borderRadius: 1.5, overflow: 'hidden' }}>
                      {slots.map((p, i) => {
                        const isOverflowSlot = i === totalSlots - 1 && overflowCount > 0;
                        return (
                          <Box key={i} sx={{ width: '100%', aspectRatio: '3/4', borderRadius: 0.75, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.08)', position: 'relative' }}>
                            {p && <ImageShimmer src={`${TMDB_W185}${p}`} alt="" width="100%" height="100%" objectFit="cover" />}
                            {isOverflowSlot && (
                              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.6)', fontFamily: font(), fontSize: cols >= 4 ? 10 : 13, fontWeight: 700, color: '#fff' }}>
                                +{overflowCount}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </>
              );
            })() : (
              /* ── Review preview ── */
              <>
                <Typography sx={{ fontFamily: font(), fontWeight: 700, fontSize: 15, color: '#fff', lineHeight: 1.3, mb: 0.25 }}>
                  {preview.row.movieTitle}
                </Typography>
                {(preview.row.director || preview.row.releaseYear) && (
                  <Typography sx={{ fontFamily: font(), fontSize: 12, color: 'rgba(255,255,255,0.6)', mb: 0.5 }}>
                    {[preview.row.director, preview.row.releaseYear].filter(Boolean).join(' — ')}
                  </Typography>
                )}
                {preview.row.rating != null && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 1 }}>
                    {[1, 2, 3, 4, 5].map((star) => {
                      const r = preview.row.rating ?? 0;
                      const full = r >= star;
                      const half = !full && r >= star - 0.5;
                      return (
                        <Box key={star} sx={{ position: 'relative', fontSize: 13, lineHeight: 1, width: '1em', height: '1em' }}>
                          {/* empty star base */}
                          <Typography component="span" sx={{ fontSize: 13, lineHeight: 1, color: 'rgba(255,255,255,0.22)', position: 'absolute', left: 0, top: 0 }}>★</Typography>
                          {/* filled overlay: full or half-width clip */}
                          {(full || half) && (
                            <Typography component="span" sx={{ fontSize: 13, lineHeight: 1, color: 'rgba(214,181,30,0.95)', position: 'absolute', left: 0, top: 0, overflow: 'hidden', width: full ? '100%' : '50%' }}>★</Typography>
                          )}
                        </Box>
                      );
                    })}
                    <Typography sx={{ fontFamily: font(), fontSize: 12, color: 'rgba(255,255,255,0.65)', ml: 0.5, lineHeight: 1 }}>
                      {preview.row.rating}/5
                    </Typography>
                  </Box>
                )}
                <Typography sx={{
                  fontFamily: font(), fontSize: 14, lineHeight: 1.55, color: 'rgba(255,255,255,0.92)',
                  display: '-webkit-box', WebkitLineClamp: 10, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {preview.row.reviewText}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Post button */}
      <Box sx={{ position: 'relative', zIndex: 10, px: 2.5, pb: 3, pt: 1.5, flexShrink: 0 }}>
        <CustomButton
          semantic="brand"
          loading={posting}
          disabled={posting}
          onClick={onPost}
          sx={{ width: '100%' }}
        >
          Post Spotlight
        </CustomButton>
      </Box>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AddStoryBottomSheet: React.FC<AddStoryBottomSheetProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const { userWatchlists } = useUserWatchlistData();
  const { showSuccess, showError } = useToast();

  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [reviews, setReviews] = useState<ReviewRow[]>([]);
  const [activeStoryIds, setActiveStoryIds] = useState<Set<string>>(new Set());
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [posting, setPosting] = useState(false);
  const mountedRef = useRef(true);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open || !user) return;
    mountedRef.current = true;
    setSearch('');
    setTab(0);
    setPreview(null);

    const load = async () => {
      setLoadingReviews(true);
      try {
        const [watchedMovies, activeStories] = await Promise.all([
          movieService.getUserWatchedMovies(user),
          getMyStories(user.uid),
        ]);

        const ids = new Set<string>();
        for (const s of activeStories) ids.add(s.id);
        if (mountedRef.current) setActiveStoryIds(ids);

        const withReviews = watchedMovies.filter((m: UserMovieData) => m.review?.trim());

        const rows = await Promise.all(
          withReviews.map(async (m: UserMovieData) => {
            let posterPath: string | null = null;
            let backdropPath: string | null = null;
            let director: string | null = null;
            let releaseYear: number | null = null;
            let cachedTitle: string | null = null;
            try {
              const cached = await movieCacheService.getMovieData(m.movieId);
              cachedTitle = cached.title ?? null;
              posterPath = cached.posterPath;
              backdropPath = cached.backdropPath;
              director = cached.directors?.[0]?.name ?? null;
              releaseYear = cached.year ? parseInt(cached.year, 10) : null;
            } catch { /* skip */ }
            return {
              movieId: m.movieId,
              movieTitle: m.movieTitle ?? cachedTitle ?? m.movieId,
              posterPath,
              backdropPath,
              director,
              releaseYear,
              rating: m.rating ?? null,
              reviewText: m.review ?? '',
              watchedDate: (m as any).watchedDate as string | undefined,
              watchedDatePrecision: (m as any).watchedDatePrecision as 'day' | 'month' | 'year' | undefined,
              updatedAt: m.updatedAt,
            } as ReviewRow;
          })
        );

        if (mountedRef.current) setReviews(rows);
      } catch (err) {
        console.error('AddStoryBottomSheet: failed to load', err);
      } finally {
        if (mountedRef.current) setLoadingReviews(false);
      }
    };

    load();
    return () => { mountedRef.current = false; };
  }, [open, user]);

  const handlePost = useCallback(async () => {
    if (!user || !preview || posting) return;
    setPosting(true);
    try {
      if (preview.kind === 'review') {
        const { row } = preview;
        await createStory({
          authorUid: user.uid,
          movieId: row.movieId,
          rating: row.rating,
          reviewText: row.reviewText,
          watchedDate: row.watchedDate,
          watchedDatePrecision: row.watchedDatePrecision,
        });
      } else {
        const { wl, topPosterPaths } = preview;
        await createWatchlistStory({
          authorUid: user.uid,
          watchlistId: wl.id,
          watchlistName: wl.name,
          description: wl.description || undefined,
          coverPath: topPosterPaths[0] ?? null,
          topPosterPaths,
          movieCount: wl.movieDetails.length,
        });
      }
      invalidateStoriesCache();
      showSuccess('Spotlight posted!');
      onClose();
    } catch (err) {
      console.error('AddStoryBottomSheet: failed to post story', err);
      showError('Failed to post spotlight. Please try again.');
    } finally {
      setPosting(false);
    }
  }, [user, preview, posting, onClose]);

  const filteredReviews = useMemo(() => {
    const q = search.toLowerCase();
    return reviews.filter((r) => {
      if (activeStoryIds.has(getReviewStoryId(user?.uid ?? '', r.movieId))) return false;
      if (q && !r.movieTitle.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [reviews, activeStoryIds, search, user?.uid]);

  const filteredWatchlists = useMemo(() => {
    const q = search.toLowerCase();
    return userWatchlists.filter((wl) => {
      if (activeStoryIds.has(getWatchlistStoryId(user?.uid ?? '', wl.id))) return false;
      if (q && !wl.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [userWatchlists, activeStoryIds, search, user?.uid]);

  const dividerColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const searchBorder = `1.5px solid ${
    searchFocused
      ? theme.palette.primary.main
      : isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'
  }`;

  // ── Picker (list) content ──
  const pickerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <BottomSheetDraggableHeader sx={{
        px: 2, py: 1.5,
        borderBottom: `1px solid ${dividerColor}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 16, color: theme.palette.text.primary }}>
          Share to spotlight
        </Typography>
        <IconButton onClick={onClose} sx={{ color: theme.palette.text.secondary }}>
          <Close />
        </IconButton>
      </BottomSheetDraggableHeader>

      {/* Search bar */}
      <Box sx={{ px: 2.5, pt: 2, pb: 0, flexShrink: 0 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          border: searchBorder, borderRadius: 3,
          px: 1.5, py: 1.25,
          transition: 'border-color 0.2s',
          bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
        }}>
          <Search sx={{ fontSize: 18, color: theme.palette.text.secondary, flexShrink: 0 }} />
          <Box
            component="input"
            ref={searchInputRef}
            placeholder={tab === 0 ? 'Search reviews…' : 'Search watchlists…'}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            sx={{
              flex: 1, border: 'none', outline: 'none', background: 'transparent',
              fontFamily: font(), fontSize: 14, color: theme.palette.text.primary,
              '&::placeholder': { color: theme.palette.text.secondary, opacity: 0.7 },
            }}
          />
          {search && (
            <IconButton
              size="small"
              onClick={() => { setSearch(''); searchInputRef.current?.focus(); }}
              sx={{ p: 0.25, color: theme.palette.text.secondary }}
            >
              <Clear sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1, flexShrink: 0 }}>
        <CustomTabs
          value={tab}
          onChange={(v) => { setTab(v); setSearch(''); }}
          tabs={TABS}
        />
      </Box>

      <Box sx={{ height: '1px', bgcolor: dividerColor, flexShrink: 0 }} />

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, pb: 3, minWidth: 0 }}>
        {tab === 0 ? (
          /* ── Reviews ── */
          loadingReviews ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25 }}>
                <Skeleton variant="rounded" width={44} height={66} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" height={16} />
                  <Skeleton variant="text" width="40%" height={13} />
                </Box>
              </Box>
            ))
          ) : filteredReviews.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: font(), fontSize: 14, color: theme.palette.text.secondary }}>
                {search
                  ? 'No matching reviews.'
                  : 'No reviews to share yet. Mark a movie watched and write your thoughts.'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
              {filteredReviews.map((row) => {
                const cardData: PublicReviewCardData = {
                  id: `${user?.uid ?? ''}_${row.movieId}`,
                  movieId: row.movieId,
                  movieTitle: row.movieTitle,
                  moviePoster: row.posterPath ? `${TMDB_W185}${row.posterPath}` : undefined,
                  rating: row.rating ?? undefined,
                  review: row.reviewText,
                  dateLabel: '',
                  updatedDateLabel: '',
                  sortValue: parseFirebaseDate(row.updatedAt)?.getTime() ?? 0,
                };
                return (
                  <PublicReviewCard
                    key={row.movieId}
                    review={cardData}
                    hideActionBar
                    onReviewClick={() => setPreview({ kind: 'review', row })}
                  />
                );
              })}
            </Box>
          )
        ) : (
          /* ── Watchlists ── */
          filteredWatchlists.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: font(), fontSize: 14, color: theme.palette.text.secondary }}>
                {search
                  ? 'No matching watchlists.'
                  : 'No watchlists to share yet. Create one from +.'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, pt: 1.5, minWidth: 0 }}>
              {filteredWatchlists.map((wl) => {
                const moviePosters = wl.movieDetails
                  .slice(0, 3)
                  .map((m) => m.poster ? `${TMDB_W185}${m.poster}` : '')
                  .filter(Boolean);
                const topPosterPaths = wl.movieDetails
                  .slice(0, 16)
                  .map((m) => m.poster ?? '')
                  .filter(Boolean);
                return (
                  <Box key={wl.id} sx={{ minWidth: 0 }}>
                    <WatchlistWidget
                      watchlistType={wl.id}
                      user={user}
                      moviePosters={moviePosters}
                      fixedHeight
                      externalWatchlistData={{
                        name: wl.name,
                        description: wl.description,
                        isPrivate: wl.isPrivate,
                        createdBy: wl.createdBy,
                        createdByName: wl.createdByName,
                        createdByAvatar: wl.createdByAvatar,
                        collaborators: wl.collaborators,
                        adminCollaboratorIds: wl.adminCollaboratorIds,
                        collaboratorProfiles: wl.collaboratorProfiles,
                        followerCount: wl.followerCount,
                        movieCount: wl.movieDetails.length,
                        movieDetails: wl.movieDetails,
                      }}
                      onClick={() => setPreview({ kind: 'watchlist', wl, topPosterPaths })}
                    />
                  </Box>
                );
              })}
            </Box>
          )
        )}
      </Box>
    </Box>
  );

  const activeContent = preview ? (
    <StoryPreview
      preview={preview}
      isDark={isDark}
      posting={posting}
      onBack={() => setPreview(null)}
      onPost={handlePost}
    />
  ) : pickerContent;

  if (!isMobile) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            height: '75dvh',
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: isDark ? '#1C1C1E' : '#FFFFFF',
          },
        }}
      >
        {activeContent}
      </Dialog>
    );
  }

  return (
    <BottomSheet open={open} onClose={onClose} height="75dvh">
      {activeContent}
    </BottomSheet>
  );
};

export default AddStoryBottomSheet;
